import { useEffect, useMemo, useState } from "react";
import { alertServerError, msToTime } from "features/helpers/utils";
import { useOutletContext } from "react-router";
import { apiUrl } from "features/configure";
import { useDispatch } from "react-redux";
import { setDiscordAgentBindingBarShow } from "features/metadesk/redux/globalState/globalState.action";
import axios from "axios";
import _ from "lodash";
import { useIntl } from "react-intl";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import { Box } from "@mui/material";
import { useFetchTeamMember } from "../redux/fetchTeamMember";
import { getUserAvatarIcon } from "../utils";
import { useTheme } from "@emotion/react";
import IconIcPin from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcPin";

import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";

export function AgentAnalytics({
  startDate,
  endDate,
  platform,
  isAgentAnalyticsLoading,
  setIsAgentAnalyticsLoading,
}) {
  const { formatMessage } = useIntl();
  const [entityName, isCustomDomain] = useOutletContext();
  const [analytics, setAnalytics] = useState();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("ticketCount.done");
  const dispatch = useDispatch();
  const { memberList } = useFetchTeamMember();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const rowData = useMemo(() => {
    let m = [];
    const agentsData = analytics?.agentMetrics;
    if (agentsData) {
      m = agentsData;
    }

    const result = _.orderBy(
      _.values(m),
      [
        (item) => (item.agentId === null ? 0 : 1),
        (item) => !item[orderBy],
        orderBy,
      ],
      ["asc", "asc", order]
    );
    return result;
  }, [analytics, order, orderBy, memberList]);

  async function getAgentData({ params }) {
    const _analytics = await axios.get(
      apiUrl + `/api/ticket/analytics/${entityName}/agent`,
      { params }
    );
    setAnalytics(_analytics.data);
  }

  useEffect(async () => {
    try {
      setIsAgentAnalyticsLoading(true);
      const params = { platform };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      await getAgentData({ params });
    } catch (error) {
      dispatch(alertServerError(error));
    } finally {
      setIsAgentAnalyticsLoading(false);
    }
  }, [startDate, endDate, platform]);

  // show discordAgentBindingBar or not
  useEffect(() => {
    const hasUnboundDiscordAgent = rowData.some(
      (agent) => agent.agentType === "discord" && !agent.discordHandle
    );

    if (hasUnboundDiscordAgent) {
      dispatch(setDiscordAgentBindingBarShow(true));
    }

    return () => {
      dispatch(setDiscordAgentBindingBarShow(false)); // hide discordAgentBindingBar when component unmounts
    };
  }, [rowData, dispatch]);

  const headers = [
    {
      show: true,
      label: formatMessage({ id: "serviceReport.headline.agentName" }),
      id: "agentName",
      width: 220,
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.ticketStatus.new" }),
      id: "ticketCount.new",
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.ticketStatus.onging" }),
      id: "ticketCount.ongoing",
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.ticketStatus.done" }),
      id: "ticketCount.done",
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.headline.aft" }),
      id: "avgFirstResponseTime",
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.headline.arv" }),
      id: "avgResolveTime",
    },
    {
      show: true,
      label: formatMessage({ id: "serviceReport.headline.csat" }),
      id: "agentCsat",
    },
  ];

  const theme = useTheme();

  const renderAgentName = (agent) => {
    const { agentType, discordHandle, agentName } = agent;
    if (agentType !== "discord" && agentType !== null) return agentName;

    const isDiscordAgent = agentType === "discord";
    const isBound = !isDiscordAgent && discordHandle;
    const iconColor = isBound || isDiscordAgent ? "#5865F2" : "#E6E6E6";

    const displayName = isDiscordAgent
      ? formatMessage({ id: "global.status.unbound" })
      : agentName;

    return (
      <>
        {!isDiscordAgent && displayName}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "10px",
            color: "#555",
          }}>
          <i
            className="meta-crm-icon-ic_discord font-size-16"
            style={{ color: iconColor }}
          />

          {isDiscordAgent ? agentName.split("#")[0] : discordHandle}
        </Box>
      </>
    );
  };

  const renderContents = (agent) => {
    const icon = getUserAvatarIcon(agent._id, theme);

    const contents = [
      <Box slot="agentName">
        {agent.agentId ? (
          <Box className="startRow">
            <img
              src={require(`assets/img/${icon}`)}
              className="icon"
              style={{ borderRadius: 20, marginRight: 8 }}
            />
            <Box>{renderAgentName(agent)}</Box>
          </Box>
        ) : (
          <Box className="startRow">
            <IconIcPin
              className="icon"
              style={{ borderRadius: 20, margin: 5 }}
            />
            {formatMessage({ id: "global.ticketStatus.unassigned" })}
          </Box>
        )}
      </Box>,
      <Box slot="ticketCount.new">{agent?.ticketCount?.new || "-"}</Box>,
      <Box slot="ticketCount.ongoing">
        {agent?.ticketCount?.ongoing || "-"}
      </Box>,
      <Box slot="ticketCount.done">{agent?.ticketCount?.done || "-"}</Box>,
      <Box slot="avgFirstResponseTime">
        {agent.avgFirstResponseTime
          ? msToTime(agent.avgFirstResponseTime)
          : "-"}
      </Box>,
      <Box slot="avgResolveTime">
        {agent.avgResolveTime ? msToTime(agent.avgResolveTime) : "-"}
      </Box>,
      <Box slot="agentCsat">
        {agent?.agentCsat || "-"}
        {agent?.csatRatingPercent
          ? ` (${parseFloat(agent?.csatRatingPercent * 100).toFixed(
              0
            )}% ${formatMessage({ id: "serviceReport.csat.rating" })})`
          : ""}
      </Box>,
    ];
    return contents;
  };

  return (
    <Box position={"relative"} sx={{ mt: "12px" }}>
      <CRMTable
        headers={headers}
        rowData={rowData}
        onRequestSort={handleRequestSort}
        order={order}
        orderBy={orderBy}
        contents={_.map(rowData, (agent) => renderContents(agent))}
        noDataComponent={
          <Box>
            <EmptyIconStatus
              icon="table"
              emptyTitle={formatMessage({ id: "emptyState.table.title" })}
              emptyDescription={formatMessage({
                id: "emptyState.table.description",
              })}
            />
          </Box>
        }
        isLoading={isAgentAnalyticsLoading}
      />
    </Box>
  );
}
